/**
 * R17 A01 (STATE-HIST) — `history/abort-batch` + `history/replay-forwards` +
 * `history/replay-inverses`, extracted VERBATIM from `editorStore.ts`'s
 * `dispatch()` body (no behaviour change). Same STORE-SPLIT lineage as
 * `editorStoreHistoryUndoRedo.ts` (companion module — see its header for the
 * `set`/`get` ≡ `useEditorStore.getState()` equivalence and the shared
 * COLLAB-LOCK-COMMAND veto-aware history rationale, which governs all three
 * handlers in this file too) and `editorStoreHistoryBatch.ts`
 * (begin-batch/commit-batch).
 *
 * `editorStore.ts`'s `dispatch()` still OWNS the `command.type ===
 * "history/abort-batch" | "history/replay-forwards" | "history/replay-inverses"`
 * routing; it delegates each branch's BODY to `handleHistoryAbortBatch` /
 * `handleHistoryReplayForwards` / `handleHistoryReplayInverses` below, then
 * returns.
 */
import {
  bumpForClasses,
  diffBumpClasses,
} from "@dxf-ai-editor/drawing-model";
import {
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
} from "@dxf-ai-editor/contracts";
import { t } from "../i18n";
import { pushAuditEvent } from "./reducerHelpers";
import { computeRevisionStateDiff } from "./editorStoreRevisionDiff";
import {
  markDispatchCollabLockVetoed,
  resetDispatchCollabLockVetoFlag,
  wasLastDispatchCollabLockVetoed,
} from "./collabLockDispatchVetoSignal";
// R17 A01 (STATE-HIST) — the host-store surface + `set`/`get` type aliases
// are declared ONCE in `editorStoreHistoryUndoRedo.ts` (as
// `HistoryOrchestrationHostStore`/`…Set`/`…Get`) and re-used here — see
// `editorStoreHistoryBatch.ts`'s matching import comment for the jscpd
// consolidation rationale. No cycle: `editorStoreHistoryUndoRedo.ts` does not
// import this file back.
import {
  type HistoryOrchestrationGet,
  type HistoryOrchestrationSet,
} from "./editorStoreHistoryUndoRedo";

// ── Phase 4.1.f — history/abort-batch ────────────────────────────
// Revert all accumulated mutations and discard the batch.
export function handleHistoryAbortBatch(
  command: Extract<
    EditorCommand | DynamicBlockCommand,
    { type: "history/abort-batch" }
  >,
  set: HistoryOrchestrationSet,
  get: HistoryOrchestrationGet,
): void {
  const { current } = get().state.history;
  if (current === null) return;
  // COLLAB-LOCK-COMMAND — P1 (post-review, round 5): track whether ANY
  // inverse in this replay loop was rejected by the fail-closed
  // collab-lock EDIT veto. Another collaborator can acquire a lock on an
  // entity/layer this batch touched sometime between the forward apply
  // (earlier in the batch) and this abort — a real possibility across
  // the batch's async window. The revision-bump computation below is
  // ALREADY diff-based (whatever survived the abort bumps, whatever
  // reverted does not — see the comment there), so it self-corrects for
  // a PARTIAL revert with no change needed. The audit/command-line
  // restore is NOT self-correcting: it unconditionally rewinds to the
  // pre-batch snapshot a few lines down, which would silently ERASE the
  // veto's own audit-error event (pushed by the recursive dispatch call
  // above, into the SAME live `state.audit` this rewind then
  // overwrites) — making a genuinely-blocked partial revert look, to the
  // user, like a clean abort. Surface a distinct summary event on top of
  // the rewind instead.
  let anyInverseVetoed = false;
  // current.inverses is already in reverse order (last command first).
  // Null slots (non-invertible commands) are skipped. BREAK on the
  // first veto (rather than continuing through the remaining inverses)
  // — this codebase's whole collab-lock veto contract is "veto the
  // WHOLE dispatch, atomically" (see `collabLockVeto.ts`'s header:
  // a partial apply is itself an AutoCAD-parity violation). Without an
  // early break, an inverse LATER in this array (an entity that is NOT
  // locked) would still apply for real even though an EARLIER inverse
  // in the same loop was blocked — silently reverting part of the batch
  // while claiming (via the stack-unchanged contract) that nothing was
  // reverted. Breaking bounds the partial-apply footprint to "process
  // the veto-free PREFIX of this array, stop at the first conflict".
  //
  // GitHub Codex review (PR #534, round 3) P1 — "Preserve batch history
  // when abort vetoes". Indexed (not `for...of`) so `i` survives the
  // break — needed below to roll back the applied PREFIX using the same
  // finding-#15 `n-1-i` sibling-array mapping `history/replay-inverses`
  // already uses for exactly this shape (`current.forwards`/
  // `current.inverses` are the SAME always-parallel, reverse-order pair
  // as a batched HistoryEntry's `forward`/`inverse` — see
  // `history/commit-batch`'s own comment: forwards APPENDS in dispatch
  // order, inverses PREPENDS, both always the same length).
  const inverseCount = current.inverses.length;
  const appliedInverseIndices: number[] = [];
  for (let i = 0; i < inverseCount; i += 1) {
    const inv = current.inverses[i];
    if (inv === null) continue;
    get().dispatch(
      { ...(inv as EditorCommand), __bypassHistory: true } as unknown as EditorCommand,
    );
    if (wasLastDispatchCollabLockVetoed()) {
      anyInverseVetoed = true;
      break;
    }
    appliedInverseIndices.push(i);
  }
  // GitHub Codex review (PR #534, round 3) P1 — ROLL BACK the applied
  // PREFIX instead of leaving it live. Before this fix, a batch that
  // edited A then B (forwards=[F_A,F_B], inverses=[inv(F_B),inv(F_A)])
  // whose abort reverted B (inv(F_B) applied for real) but then hit a
  // fresh lock on A (inv(F_A) vetoed) left B genuinely reverted while A
  // stayed at its batch-applied value — and the code below UNCONDITIONALLY
  // cleared `history.current` regardless of the veto, discarding the
  // ONLY history entry that could ever have undone A again. The
  // surviving A mutation became permanently non-undoable. Mirrors
  // `history/replay-inverses`' OWN finding-#15 rollback one-for-one:
  // `appliedInverseIndices[k]` (an index into the REVERSED `inverses`
  // array) undid `current.forwards[inverseCount-1-appliedInverseIndices[k]]`
  // — re-dispatch those forwards, in ASCENDING original-index order (the
  // map already produces this), to restore exactly what the applied
  // prefix just reverted. After this, the document is back to its FULL
  // pre-abort-attempt state (A and B both still applied) — abort becomes
  // a genuine no-op instead of a silent partial revert.
  if (anyInverseVetoed && appliedInverseIndices.length > 0) {
    const forwardCount = current.forwards.length;
    const rollbackForwardIndices = appliedInverseIndices
      .map((i) => forwardCount - 1 - i)
      .sort((a, b) => a - b);
    for (const fi of rollbackForwardIndices) {
      const fwd = current.forwards[fi];
      if (fwd == null) continue; // defensive — forwards carries no nulls in practice
      get().dispatch(
        { ...(fwd as EditorCommand), __bypassHistory: true } as unknown as EditorCommand,
      );
    }
  }
  // Propagate the aggregate to a possible OUTER caller. `abort-batch` is
  // a standalone command today (not itself dispatched from undo/redo's
  // own recursive call), but a future caller nested one level up must
  // see the aggregate outcome, not a stale single-iteration value left
  // over from the last loop entry above.
  if (anyInverseVetoed) markDispatchCollabLockVetoed(); else resetDispatchCollabLockVetoFlag();
  // GitHub Codex review (PR #534, round 3) P1 — a vetoed abort (rolled
  // back to the full pre-abort-attempt state above) must NOT finalize:
  // leave `history.current` / `pendingBatchBaseDoc` / `pendingBatchBaseState`
  // / `pendingBatchBaseAuditSequence` / selection / audit / commandLine /
  // propertyWorkflows completely untouched (the batch stays open, exactly
  // as a single vetoed undo/redo leaves `past`/`future` untouched — see
  // those handlers' own "stays in past for a retry" comments) so the user
  // can retry the SAME abort once the lock releases. Only push the
  // distinct partial-block notice on top of the live (not rewound) audit
  // ring.
  if (anyInverseVetoed) {
    set((s) => ({
      state: pushAuditEvent(
        s.state,
        command,
        t("collab.lock.abort_batch_partial"),
        "error",
      ),
    }));
    return;
  }
  // P2: restore selection to the snapshot taken at begin-batch.
  const selectionBefore = [...current.selectionBefore];
  set((s) => {
    // Gate #4 round-2 (FIX A — P2 editorStore.ts:3579) — RESTORE the
    // capped audit/command-line FEEDS from the PRE-BATCH SNAPSHOT, do NOT
    // filter the mutated arrays. SYMMETRIC with the geometry inverse-replay
    // above: on COMMIT the turn's audit events legitimately stream; on ABORT
    // the turn never happened, so the audit ring + command-line transcript
    // must return to EXACTLY their pre-batch contents.
    //
    // # Why FILTERING was wrong (the round-2 P2)
    //
    // `audit.events` / `audit.uiEvents` / `commandLine.transcript` are each
    // newest-N capped (24/48/24 — see reducerHelpers.ts `appendAuditEvent`).
    // A >24-command aborted batch's in-batch `pushAuditEvent`s already SLICED
    // the OLDER pre-batch rows OUT of the live arrays to honour the cap.
    // Filtering the post-batch capped arrays by `sequence < base` only
    // removes the aborted rows — it CANNOT bring back the cap-evicted
    // pre-batch rows, so an abort after a populated history left the
    // History/command-line feeds MISSING pre-batch entries.
    //
    // # The fix
    //
    // `pendingBatchBaseState` is the WHOLE pre-batch shell state captured by
    // reference at the OUTERMOST begin-batch (immutable snapshot). Restoring
    // its `audit` (events + uiEvents + nextSequence) and
    // `commandLine.transcript` BOTH removes the aborted rows AND recovers the
    // cap-evicted pre-batch rows — the snapshot holds exactly the pre-batch
    // contents. `pendingBatchBaseAuditSequence` and `pendingBatchBaseState`
    // are captured together on the same outermost begin, so they are both
    // non-null or both null; a stray abort with no recorded begin (base ===
    // null) leaves the live feeds untouched (no-op, matching the prior
    // behaviour). The telemetry subscriber's defer-while-open half (see
    // `installAuditTelemetryTransport`) guarantees no in-batch event was
    // projected/flushed before this restore runs.
    const baseState = s.pendingBatchBaseState;
    const rewoundAudit =
      baseState !== null
        ? {
            ...s.state.audit,
            nextSequence: baseState.audit.nextSequence,
            // Fresh array copies so a future in-place mutation of the live
            // ring can never write through into the retained snapshot.
            events: [...baseState.audit.events],
            uiEvents: [...(baseState.audit.uiEvents ?? [])],
          }
        : s.state.audit;
    const rewoundCommandLine =
      baseState !== null
        ? {
            ...s.state.commandLine,
            transcript: [...baseState.commandLine.transcript],
          }
        : s.state.commandLine;
    // github PR #527 round-21 finding #5 — a batched `SET_ACTIVE_LAYER` /
    // `setCurrentDimensionStyle`-style current-pointer change dispatches a
    // no-selection `properties/apply` (`entityIds: []`), which
    // `computeInverse` (geometryMutations.ts) DELIBERATELY returns `null`
    // for — that function is pure over `DrawingDocument` and has no view
    // of the SHELL-level `propertyWorkflows` the command actually
    // mutated, so it cannot build a restoring inverse. The replay loop
    // above therefore SKIPS this command's null slot (same as every
    // other non-invertible command), leaving `defaultObjectProperties` /
    // `matchpropSourceEntityId` at whatever the aborted batch left them
    // at instead of their pre-batch value — the ABB-S1 round-4
    // GENERALIZATION 2 diff below only computes which revision CLASSES
    // to bump for telemetry, it does not restore state. Symmetric with
    // `audit`/`commandLine` immediately above: restore the WHOLE
    // `propertyWorkflows` bag from the pre-batch snapshot so every
    // current-pointer field (present today or added later) reverts,
    // not just `Layer`.
    const rewoundPropertyWorkflows =
      baseState !== null ? baseState.propertyWorkflows : s.state.propertyWorkflows;
    const postAbortState: EditorShellState = {
      ...s.state,
      history: { ...s.state.history, current: null },
      selection: { ...s.state.selection, entityIds: selectionBefore },
      audit: rewoundAudit,
      commandLine: rewoundCommandLine,
      propertyWorkflows: rewoundPropertyWorkflows,
    };
    // GitHub Codex review (PR #534, round 3) P1 — this block is now only
    // reached when `anyInverseVetoed` is `false`: a genuinely vetoed
    // abort rolls back its applied prefix and returns EARLY (a few dozen
    // lines up), before ever touching `history.current` / the audit
    // rewind below, so the batch stays open for a retry instead of being
    // silently finalized over a still-live partial mutation. What
    // follows is therefore always the CLEAN, fully-reverted abort path —
    // no veto-notice branch needed here anymore (see
    // `collab.lock.abort_batch_partial`'s new call site above instead).
    // ABB-S1 round-4 GENERALIZATION 2 — bump from the NET SURVIVING DIFF, the
    // same diff-everything mechanism undo/redo uses. Instead of folding
    // per-persist-kind accumulators (round-3 covered only DIRECT-SET, missing
    // batched-null-inverse mutations that ALSO persist past abort — a batched
    // command whose inverse slot is null isn't reverted by the replay loop
    // above), diff the pre-batch BASE state vs the POST-abort state: WHATEVER
    // actually survived the abort (direct-set table edit, batched-null-inverse
    // shell/layout mutation, or any future abort-persist) bumps; whatever was
    // reverted (base ⇔ post-abort identical) bumps nothing. One move closes
    // the whole abort-persist class. The accumulators are DISCARDED (their
    // role is superseded by the net diff for the abort path).
    const base =
      s.pendingBatchBaseDoc !== null && s.pendingBatchBaseState !== null
        ? bumpForClasses(
            s.revisions,
            diffBumpClasses(
              computeRevisionStateDiff(
                s.pendingBatchBaseDoc,
                s.geometryDocument,
                s.pendingBatchBaseState,
                postAbortState,
              ),
            ),
          )
        : s.revisions;
    return {
      state: postAbortState,
      revisions: base,
      pendingBumpClasses: null,
      pendingDirectSetBumpClasses: null,
      pendingBatchBaseDoc: null,
      pendingBatchBaseState: null,
      pendingBatchBaseAuditSequence: null,
      // Gate #4 round-2 (FIX C) — EXPLICIT abort close: the subscriber
      // DISCARDS this turn's staged telemetry (never posted).
      lastBatchClosedBy: "abort",
    };
  });
}

// ── Phase 4.1.f — history/replay-forwards / history/replay-inverses ─
// Synthetic commands produced by commit-batch and stored as the
// forward/inverse of a batched HistoryEntry. Re-dispatch each command
// in batch order with __bypassHistory=true so mutations apply without
// being re-recorded.
//
// ── P1 (post-review, round 6, finding #15) — roll back a partially-
// applied prefix instead of leaving it live ────────────────────────────
// The loop applies sub-commands ONE AT A TIME and breaks the MOMENT it
// hits a veto — every sub-command BEFORE that point has, by then,
// already been applied FOR REAL (`commitMutation` already ran). The
// caller (`undo()`/`redo()`) only sees the AGGREGATE `anyVetoed` flag
// and, correctly (round 5), leaves `past`/`future` untouched on a veto —
// but that DESYNCS the stacks from the document: the stacks say "this
// batch never happened" while a real prefix of it did.
//
// A dry-run PREFLIGHT (checking every sub-command's veto before applying
// any of them) was considered and REJECTED: `EditorCommand`s that MINT a
// fresh entity id (CREATE-family, un-stamped — see `recordedForward`'s
// doc comment: a bare `geometry/create-line` carries no explicit id) get
// a DIFFERENT id depending on how many entities already exist at dispatch
// time. A dry run never commits, so the document never advances between
// dry-run iterations — the SECOND create in a preflighted batch would be
// checked against the WRONG (stale) predicted id, silently missing a
// real conflict. Real sequential apply does not have this problem (each
// commit correctly advances the id-minting basis for the next command),
// so instead: apply for REAL as before, and if a veto stops the loop
// partway, ROLL BACK exactly the applied prefix using the SIBLING array
// (`siblingBatch`, threaded onto the command by `history/commit-batch`)
// — the each-applied-command's OWN known inverse/forward, not a
// prediction. Because the rollback re-dispatches against the document
// state the applied prefix ACTUALLY left behind (not a guess), it
// reproduces minted ids correctly too (see the two handlers' own
// comments for the exact index math + a worked example).
export function handleHistoryReplayForwards(
  command: Extract<
    EditorCommand | DynamicBlockCommand,
    { type: "history/replay-forwards" }
  >,
  get: HistoryOrchestrationGet,
): void {
  // COLLAB-LOCK-COMMAND — P1 (post-review, round 5): a BATCHED
  // HistoryEntry's `forward` IS this exact command — `redo()` dispatches
  // it directly via `entry.forward`. Accumulate whether ANY sub-command
  // in the batch was rejected by the fail-closed collab-lock EDIT veto,
  // then set the module flag to the AGGREGATE before this branch's own
  // `return` so `redo()`'s post-dispatch check (a few hundred lines up)
  // sees the true outcome instead of a stale single-iteration value left
  // by whichever sub-command dispatched last. BREAK on the first veto —
  // mirrors `history/abort-batch`'s inverse loop: this codebase's whole
  // collab-lock veto contract is "veto the WHOLE dispatch, atomically"
  // (`collabLockVeto.ts`'s header), so a sub-command LATER in the batch
  // must not silently apply for real just because it happens to touch
  // an entity/layer nobody locked — that would be a partial apply the
  // stack-unchanged contract cannot honestly represent.
  let anyVetoed = false;
  const n = command.batch.length;
  const appliedIndices: number[] = [];
  for (let i = 0; i < n; i += 1) {
    const cmd = command.batch[i];
    get().dispatch(
      { ...(cmd as EditorCommand), __bypassHistory: true } as unknown as EditorCommand,
    );
    if (wasLastDispatchCollabLockVetoed()) {
      anyVetoed = true;
      break;
    }
    appliedIndices.push(i);
  }
  // ROLLBACK (finding #15) — `command.batch[i]` (a forward, dispatch
  // order) has its inverse at `siblingBatch[n-1-i]` (inverses are
  // stored REVERSED — see `history/commit-batch`). Undo the applied
  // PREFIX in reverse-of-application order (undo the most-recently-
  // applied one first): that is exactly `siblingBatch` sliced to its
  // LAST `appliedIndices.length` entries, walked in ARRAY order.
  // Worked example (n=2, forwards=[F0,F1], inverses=[G0=inv(F1),
  // G1=inv(F0)]): F0 applies, F1 is vetoed → appliedIndices=[0] →
  // rollback = inverses.slice(2-1) = [G1] = inv(F0) → undoes F0. ✓.
  if (anyVetoed && appliedIndices.length > 0) {
    const siblingBatch = (
      command as unknown as { siblingBatch?: ReadonlyArray<EditorCommand | null> }
    ).siblingBatch;
    if (siblingBatch !== undefined) {
      const rollbackSlice = siblingBatch.slice(siblingBatch.length - appliedIndices.length);
      for (const inv of rollbackSlice) {
        if (inv === null) continue; // non-invertible — pre-existing degraded-fidelity gap (see commit-batch's own console.warn), not a NEW regression
        get().dispatch(
          { ...inv, __bypassHistory: true } as unknown as EditorCommand,
        );
      }
    }
  }
  if (anyVetoed) markDispatchCollabLockVetoed(); else resetDispatchCollabLockVetoFlag();
}

export function handleHistoryReplayInverses(
  command: Extract<
    EditorCommand | DynamicBlockCommand,
    { type: "history/replay-inverses" }
  >,
  get: HistoryOrchestrationGet,
): void {
  // COLLAB-LOCK-COMMAND — P1 (post-review, round 5): mirror
  // `history/replay-forwards` — a BATCHED HistoryEntry's `inverse` IS
  // this exact command, and `undo()` dispatches it directly via
  // `entry.inverse`. Accumulate + propagate the aggregate veto outcome
  // the same way, breaking on the first veto for the same atomicity
  // reason.
  let anyVetoed = false;
  const n = command.batch.length;
  const appliedIndices: number[] = [];
  for (let i = 0; i < n; i += 1) {
    const cmd = command.batch[i];
    if (cmd === null) continue; // null = non-invertible command; skip
    get().dispatch(
      { ...(cmd as EditorCommand), __bypassHistory: true } as unknown as EditorCommand,
    );
    if (wasLastDispatchCollabLockVetoed()) {
      anyVetoed = true;
      break;
    }
    appliedIndices.push(i);
  }
  // ROLLBACK (finding #15) — `command.batch[i]` (an inverse, REVERSED
  // order) undid `siblingBatch[n-1-i]` (its forward, dispatch order).
  // REDO the applied PREFIX's forwards, in ORIGINAL dispatch order
  // (oldest first): map each applied inverse-index to its forward-
  // index (`n-1-i`) and sort ascending. Worked example (n=2,
  // inverses=[G0=inv(F1),G1=inv(F0)], forwards=[F0,F1]): G0 (undoes
  // F1) applies, G1 (undoes F0) is vetoed → appliedIndices=[0] →
  // forward-index = 2-1-0 = 1 → redo forwards[1] = F1 → recreates
  // what G0 just deleted. ✓. Re-dispatching against the document state
  // the applied prefix ACTUALLY left (F0's entity still present,
  // exactly as when F1 was ORIGINALLY dispatched) — not a prediction —
  // is what makes this correct even for un-stamped CREATE commands
  // whose minted id depends on the current entity count.
  if (anyVetoed && appliedIndices.length > 0) {
    const siblingBatch = (
      command as unknown as { siblingBatch?: ReadonlyArray<EditorCommand | null> }
    ).siblingBatch;
    if (siblingBatch !== undefined) {
      const forwardIndices = appliedIndices.map((i) => n - 1 - i).sort((a, b) => a - b);
      for (const fi of forwardIndices) {
        const fwd = siblingBatch[fi];
        if (fwd == null) continue; // forwards carries no nulls in practice; defensive only
        get().dispatch(
          { ...fwd, __bypassHistory: true } as unknown as EditorCommand,
        );
      }
    }
  }
  if (anyVetoed) markDispatchCollabLockVetoed(); else resetDispatchCollabLockVetoFlag();
}
